import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { err, ok } from 'neverthrow';
import { CocoChatModel } from '../models/coco-chat.model';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { toast } from '@cotera/client/app/components/ui/toaster';
import {
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';

// No required fields: the palette only surfaces actions that can run with an
// empty payload, and the model + conversation are read from the targeted model.
const inputSchema = z.object({});

type Params = z.infer<typeof inputSchema>;

export class CocoCompactAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Compact Conversation';
  inputSchema = inputSchema;
  group = 'Chat';
  description = 'Summarize earlier messages to free up context';
  icon = 'collapse';

  constructor(private readonly client: TenantedClient['resource']['chat']) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    _params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return err([{ t: 'error', message: 'Coco chat model not found' }]);
    }

    const status = model.statusWatchable.snapshot();
    if (status === 'streaming' || status === 'submitted') {
      const message =
        'Wait for the current response to finish before compacting';
      toast.error(message);
      return err([{ t: 'error', message }]);
    }

    // Stall the chat while compaction runs (parity with other assistants).
    // Optimistically flip to a busy status so the input disables immediately;
    // the server-driven status (compaction runs as a child with active stack
    // frames) then keeps it busy and reclaims this override, returning the chat
    // to 'ready' once the summary is written.
    model.statusWatchable.set('submitted');
    const loadingToast = toast.loading('Compacting conversation...', {
      id: 'compact-conversation',
    });

    const result = await this.client.compact({
      conversationId: model.conversationId,
      model: model.model,
    });

    if (result.isErr()) {
      // Clear the optimistic busy state; on failure there are no frames to
      // reconcile the override away.
      model.statusWatchable.set('ready');
      const message =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to compact conversation';
      loadingToast.error(message);
      return err([{ t: 'error', message }]);
    }

    loadingToast.success('Conversation compacted');
    return ok({});
  }

  track({
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    return cocoActionMetadata(CocoEvent.ConversationCompacted, {
      conversationId: model.conversationId,
      surface: getChatSurface(context),
    });
  }
}
